import { useEffect } from 'react';
import TrainingEnrollForm from '@/components/feature/TrainingEnrollForm';
import { formatSignFee } from '@/services/adapters';

interface TrainingEnrollModalProps {
  open: boolean;
  onClose: () => void;
  /** 活动 ID（报名接口入参） */
  activityId: number;
  topic: string;
  date: string;
  hospital: string;
  /** 活动报名费（元，0 表示免费） */
  signFee: number;
  /** 待支付报名单 Id（详情页"继续支付"传入，直接进入扫码支付步骤） */
  initialSignId?: number;
}

export default function TrainingEnrollModal({
  open,
  onClose,
  activityId,
  topic,
  date,
  hospital,
  signFee,
  initialSignId = 0,
}: TrainingEnrollModalProps) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 animate-modal-fade bg-foreground-950/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md max-h-[88vh] overflow-y-auto rounded-lg border border-background-200 bg-background-50 p-6 md:p-7 animate-modal-pop"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="在线报名"
      >
        <button
          type="button"
          aria-label="关闭"
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="flex items-center gap-2 mb-2">
          <span className="w-1.5 h-4 rounded-full bg-primary-500"></span>
          <span className="text-sm font-medium text-primary-600">在线报名</span>
        </div>
        <h3 className="font-heading text-lg font-bold leading-snug text-foreground-900">
          填写报名信息
        </h3>
        <p className="mt-1.5 text-sm text-foreground-600">
          会务组将在 1-2 个工作日内与您电话确认
        </p>

        <div className="mt-4 rounded-md bg-background-100 border border-background-200 px-4 py-3 space-y-1.5">
          <p className="text-sm font-medium text-foreground-900 leading-snug">{topic}</p>
          <p className="flex items-center gap-2 text-xs text-foreground-600">
            <i className="ri-calendar-event-line text-primary-600"></i>
            {date}
          </p>
          <p className="flex items-center gap-2 text-xs text-foreground-600">
            <i className="ri-map-pin-line text-primary-600"></i>
            {hospital}
          </p>
          <p className="flex items-center gap-2 text-xs text-foreground-600">
            <i className="ri-money-cny-circle-line text-primary-600"></i>
            报名费用：
            <span className={signFee > 0 ? 'font-medium text-primary-600' : ''}>{formatSignFee(signFee)}</span>
          </p>
        </div>

        <div className="mt-5">
          <TrainingEnrollForm
            topic={topic}
            activityId={activityId}
            signFee={signFee}
            initialSignId={initialSignId}
          />
        </div>
      </div>
    </div>
  );
}